import { match } from 'ts-pattern';
import Alert from '~/components/Alert';
import Submit from '~/components/button/Submit';
import CustomerInfoFields from '~/components/CustomerInfoFields';
import Form from '~/components/Form';
import Input from '~/components/form-controls/Input';
import Page from '~/components/layout/page/Page';
import { AddDiscountCodeForm } from './AddDiscountCodeForm';
import { ModifyQuantityForm } from './ModifyQuantityForm';
import {
  editActionTypeFormKey,
  useActionResult,
  useLoaderData,
} from './orderEditStage';
import { UpdateShippingLineForm } from './UpdateShippingLineForm';

type LoaderData = Awaited<ReturnType<typeof useLoaderData>>;

function UpdateAddressForm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'UPDATE_SHIPPING_ADDRESS' }>;
}) {
  const { shippingAddress } = data;

  return (
    <Form name="addressForm">
      <input type="hidden" name={editActionTypeFormKey} value={data.editType} />
      <CustomerInfoFields
        omitEmail
        info={{
          firstName: shippingAddress?.firstName,
          lastName: shippingAddress?.lastName,
          address: shippingAddress,
        }}
      />
      <Submit>Continue</Submit>
    </Form>
  );
}

function UpdateEmailForm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'UPDATE_EMAIL' }>;
}) {
  return (
    <Form name="emailForm">
      <input type="hidden" name={editActionTypeFormKey} value={data.editType} />
      <Input
        id="email"
        name="email"
        type="email"
        label="Email Address"
        defaultValue={data.currentEmail}
        required
        autoComplete="email"
        validationMessages={{
          valueMissing: 'Please enter an email address.',
          typeMismatch: 'Please enter a valid email address.',
        }}
      />
      <Submit>Update Email</Submit>
    </Form>
  );
}

export default function OrderEditStagePage() {
  const loaderData = useLoaderData();
  const actionResult = useActionResult();

  if ('ok' in loaderData) {
    return (
      <Page.Root title="Error" hideHeader>
        <Page.Section>
          <Alert variant="error" title={loaderData.message} />
        </Page.Section>
      </Page.Root>
    );
  }

  const content = match(loaderData)
    .with({ editType: 'UPDATE_SHIPPING_ADDRESS' }, (data) => (
      <UpdateAddressForm data={data} />
    ))
    .with({ editType: 'MODIFY_QUANTITY' }, (data) => (
      <ModifyQuantityForm data={data} />
    ))
    .with({ editType: 'UPDATE_EMAIL' }, (data) => (
      <UpdateEmailForm data={data} />
    ))
    .with({ editType: 'UPDATE_SHIPPING_LINE' }, (data) => (
      <UpdateShippingLineForm data={data} />
    ))
    .with({ editType: 'ADD_DISCOUNT_CODE' }, (data) => (
      <AddDiscountCodeForm data={data} />
    ))
    .exhaustive();

  return (
    <Page.Root title={loaderData.headline} hideHeader>
      <Page.Section>{content}</Page.Section>
      {actionResult?.ok === false && (
        <Alert variant="error" title={actionResult.message} />
      )}
    </Page.Root>
  );
}
